import { useCallback, useEffect, useRef, useState } from "react";
import { types } from "@vilocnv/allsetra-core";
import { useLastTrips } from "./useLastTrips";

/**
 * Shared panel orchestration for Last trips on dashboard Map and Objects map.
 */
export function useLastTripsPanelController(
  accountId: string | undefined,
  selectedObjectId: string | null | undefined
) {
  const [lastTripsPanelOpen, setLastTripsPanelOpen] = useState(false);
  const [lastTripsObjectName, setLastTripsObjectName] = useState<string | null>(
    null
  );
  const lastTripsObjectIdRef = useRef<string | null>(null);
  const accountIdRef = useRef(accountId);
  const {
    trips: lastTrips,
    loading: lastTripsLoading,
    error: lastTripsError,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    fetchLastTrips,
    selectTrip,
    reset: resetLastTrips
  } = useLastTrips(accountId);

  const handleCloseLastTripsPanel = useCallback(() => {
    setLastTripsPanelOpen(false);
    setLastTripsObjectName(null);
    resetLastTrips();
    lastTripsObjectIdRef.current = null;
  }, [resetLastTrips]);

  const handleOpenLastTripsPanel = useCallback(
    (objectId?: string, objectName?: string) => {
      const id = objectId ?? selectedObjectId;
      if (!id) return;

      lastTripsObjectIdRef.current = id;
      if (objectName) {
        setLastTripsObjectName(objectName);
      }
      setLastTripsPanelOpen(true);
      fetchLastTrips(id);
    },
    [fetchLastTrips, selectedObjectId]
  );

  const handleSelectLastTrip = useCallback(
    (trip: types.IObjectRide) => {
      const id = lastTripsObjectIdRef.current ?? selectedObjectId;
      if (!id) return;
      selectTrip(trip, id);
    },
    [selectTrip, selectedObjectId]
  );

  useEffect(() => {
    if (accountIdRef.current === accountId) return;

    accountIdRef.current = accountId;
    handleCloseLastTripsPanel();
  }, [accountId, handleCloseLastTripsPanel]);

  useEffect(() => {
    if (!lastTripsPanelOpen) return;

    // Close only when selection moves to a *different* object. Closing the
    // marker popup (selectedObjectId → null) must leave the panel open.
    if (
      selectedObjectId &&
      lastTripsObjectIdRef.current &&
      lastTripsObjectIdRef.current !== selectedObjectId
    ) {
      handleCloseLastTripsPanel();
    }
  }, [selectedObjectId, lastTripsPanelOpen, handleCloseLastTripsPanel]);

  return {
    lastTripsPanelOpen,
    lastTripsObjectName,
    lastTrips,
    lastTripsLoading,
    lastTripsError,
    selectedTripId,
    selectedTrip,
    rideLocations,
    locationsLoading,
    locationsError,
    handleOpenLastTripsPanel,
    handleCloseLastTripsPanel,
    handleSelectLastTrip
  };
}
